import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import Logo from "@/components/Logo";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, Tent, MapPin, Flag, Layers, PenLine, Move } from "lucide-react";
import FlashCardActivity from "@/components/activities/FlashCardActivity";
import QuickWriteActivity from "@/components/activities/QuickWriteActivity";
import DragDropMatchActivity from "@/components/activities/DragDropMatchActivity";
import QuizFlow from "@/components/QuizFlow";

const STAGE_META = {
  base_camp: { label: "Base Camp", icon: Tent },
  trail_1: { label: "Trail 1", icon: MapPin },
  trail_2: { label: "Trail 2", icon: MapPin },
  trail_3: { label: "Trail 3", icon: MapPin },
  summit: { label: "Summit", icon: Flag },
};
const ACTIVITY_META = {
  flashcards: { icon: Layers, label: "Flashcards", ap: 15 },
  quick_write: { icon: PenLine, label: "Quick Write", ap: 20 },
  drag_match: { icon: Move, label: "Drag & Match", ap: 20 },
};

const StudentExpeditionView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [exp, setExp] = useState(null);
  const [activeStage, setActiveStage] = useState(null);
  const [quizMode, setQuizMode] = useState(false);
  const [busy, setBusy] = useState(null);
  const [quizSeed, setQuizSeed] = useState(0);

  const load = async () => {
    try {
      const r = await api.get(`/student/expeditions/${id}`);
      setExp(r.data);
      if (!activeStage && r.data.stages?.length) {
        const idx = Math.min(r.data.current_stage_index || 0, r.data.stages.length - 1);
        setActiveStage(r.data.stages[idx]);
      }
    } catch (err) {
      if (err.response?.status === 401) navigate("/student/login", { replace: true });
      else toast.error("Could not load expedition");
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const onComplete = async () => {
    setQuizMode(false);
    await load();
    const refreshed = await api.get(`/student/expeditions/${id}`);
    const next = refreshed.data.stages.find((s) => !refreshed.data.completed_stages.includes(s.name));
    if (next) setActiveStage(next);
  };

  const doActivity = async (stageName, activityIndex, payload = {}) => {
    const key = `${stageName}:${activityIndex}`;
    setBusy(key);
    try {
      await api.post(`/expeditions/${id}/complete-activity`, {
        stage_name: stageName, activity_index: activityIndex, ...payload,
      });
      toast.success(`+${ACTIVITY_META[activityIndex]?.ap || 15} AP earned!`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not complete");
    } finally {
      setBusy(null);
    }
  };

  if (!exp) return <div className="min-h-screen bg-mm-parchment"><div className="text-center py-20 text-mm-navy/60">Loading trail...</div></div>;

  const completed = exp.completed_stages || [];
  const shuffledQuiz = activeStage?.quiz || [];

  return (
    <div className="min-h-screen bg-mm-parchment bg-topo">
      <header className="sticky top-0 z-20 w-full bg-mm-parchment/95 backdrop-blur border-b border-mm-navy/10">
        <div className="container mx-auto flex items-center justify-between py-3 max-w-6xl">
          <Logo size={40} />
          <Link to="/student/dashboard" className="text-sm font-bold text-mm-steel hover:text-mm-navy">← Trail HQ</Link>
        </div>
      </header>

      <div className="container mx-auto py-8 max-w-6xl">
        <div className="text-xs uppercase tracking-[0.22em] font-bold text-mm-steel">Expedition</div>
        <h1 className="font-heading text-2xl sm:text-3xl font-black text-mm-navy mt-1" data-testid="student-exp-title">{exp.topic}</h1>

        <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-1 space-y-2" data-testid="student-stage-list">
            {exp.stages.map((s, idx) => {
              const meta = STAGE_META[s.name] || { label: s.name, icon: MapPin };
              const Icon = meta.icon;
              const done = completed.includes(s.name);
              const isActive = activeStage?.name === s.name;
              return (
                <button
                  key={s.name}
                  onClick={() => { setActiveStage(s); setQuizMode(false); }}
                  className={`w-full text-left p-3 rounded-xl border-2 flex items-center gap-3 transition-all ${
                    isActive ? "border-mm-gold bg-mm-gold/10" :
                    done ? "border-mm-steel bg-mm-steel/5" :
                    "border-mm-navy/15 bg-white hover:border-mm-steel"
                  }`}
                  data-testid={`student-stage-tab-${s.name}`}
                >
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${done ? "bg-mm-steel text-white" : "bg-mm-steel/15 text-mm-navy"}`}>
                    {done ? <CheckCircle2 size={18} strokeWidth={2.5} /> : <Icon size={18} strokeWidth={2.5} />}
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest font-bold text-mm-steel">Stage {idx + 1}</div>
                    <div className="font-heading font-bold text-mm-navy">{s.title}</div>
                  </div>
                </button>
              );
            })}
          </div>

          <div className="lg:col-span-2 stage-card p-6" data-testid="student-stage-detail">
            {activeStage && !quizMode && (
              <>
                <div className="text-xs uppercase tracking-widest font-bold text-mm-steel">{STAGE_META[activeStage.name]?.label}</div>
                <h2 className="font-heading text-2xl font-black text-mm-navy mt-1">{activeStage.title}</h2>

                {(activeStage.objectives || []).length > 0 && (
                  <div className="mt-4 rounded-xl border-2 border-mm-navy/10 bg-mm-parchment p-4" data-testid="student-stage-objectives">
                    <div className="text-xs uppercase tracking-widest font-bold text-mm-steel">You'll learn to</div>
                    <ul className="mt-2 space-y-1.5 text-sm text-mm-navy list-disc pl-5">
                      {(activeStage.objectives || []).map((o, oi) => (<li key={oi}>{o}</li>))}
                    </ul>
                  </div>
                )}

                {(activeStage.explanation || activeStage.content) && (
                  <div className="mt-5" data-testid="student-stage-reading">
                    <div className="text-xs uppercase tracking-widest font-bold text-mm-steel">Reading</div>
                    <div className="mt-2 text-mm-navy/85 leading-relaxed whitespace-pre-line text-[15px]">
                      {activeStage.explanation || activeStage.content}
                    </div>
                  </div>
                )}

                {(activeStage.activities || []).length > 0 && (
                  <div className="mt-6 space-y-3">
                    <div className="text-xs uppercase tracking-widest font-bold text-mm-steel">Activities</div>
                    {activeStage.activities.map((a, i) => {
                      const compKey = `${activeStage.name}:${i}`;
                      if (typeof a === "string") return null;
                      // quick_write has been retired — hide it in older expeditions.
                      if (a.type === "quick_write") return null;
                      const meta = ACTIVITY_META[a.type] || ACTIVITY_META.flashcards;
                      const Icon = meta.icon;
                      const isDone = !!exp.activity_completions?.[compKey];
                      return (
                        <details key={compKey} className="rounded-2xl border border-mm-navy/10 bg-white overflow-hidden" open={!isDone} data-testid={`student-activity-${i}`}>
                          <summary className="px-4 py-3 cursor-pointer flex items-center justify-between gap-3 hover:bg-mm-parchment/50">
                            <div className="flex items-center gap-3">
                              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isDone ? "bg-mm-steel text-white" : "bg-mm-steel/15 text-mm-navy"}`}>
                                {isDone ? <CheckCircle2 size={18} strokeWidth={2.5} /> : <Icon size={18} strokeWidth={2.5} />}
                              </div>
                              <div>
                                <div className="text-[10px] uppercase tracking-widest font-bold text-mm-steel">{meta.label} · +{meta.ap} AP</div>
                                <div className="font-heading font-bold text-mm-navy">{a.title || meta.label}</div>
                              </div>
                            </div>
                            {isDone && <span className="text-xs font-bold text-mm-steel uppercase tracking-widest">Done</span>}
                          </summary>
                          <div className="px-4 pb-4 pt-2 border-t border-mm-navy/10">
                            {a.type === "flashcards" && <FlashCardActivity activity={a} completed={isDone} onComplete={() => doActivity(activeStage.name, i)} busy={busy === compKey} />}
                            {a.type === "quick_write" && <QuickWriteActivity activity={a} completed={isDone} completion={exp.activity_completions?.[compKey]} onComplete={(p) => doActivity(activeStage.name, i, p)} busy={busy === compKey} />}
                            {a.type === "drag_match" && <DragDropMatchActivity activity={a} completed={isDone} onComplete={() => doActivity(activeStage.name, i)} busy={busy === compKey} />}
                          </div>
                        </details>
                      );
                    })}
                  </div>
                )}

                {!completed.includes(activeStage.name) && (
                  <div className="mt-6">
                    <button onClick={() => { setQuizSeed((s) => s + 1); setQuizMode(true); }} className="btn-trail btn-gold" data-testid="student-start-quiz-btn">
                      Take the trail check
                    </button>
                  </div>
                )}
              </>
            )}
            {activeStage && quizMode && (
              <QuizFlow
                key={quizSeed}
                expeditionId={id}
                stage={{ ...activeStage, quiz: shuffledQuiz }}
                originalIndex={(qi) => qi}
                onExit={() => setQuizMode(false)}
                onComplete={onComplete}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentExpeditionView;
